/**
 * useReducedMotion hook - Responsive animation control
 * Determines whether animations should be enabled based on:
 * 1. Screen size (mobile vs desktop)
 * 2. User's motion preferences
 * 3. Performance considerations
 */

import { useEffect, useState } from 'react'

interface MotionPreferences {
  isMobile: boolean
  prefersReducedMotion: boolean
  shouldAnimate: boolean
}

const useReducedMotion = (): MotionPreferences => {
  const [preferences, setPreferences] = useState<MotionPreferences>({
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
    prefersReducedMotion: typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
    shouldAnimate: typeof window !== 'undefined'
      ? window.innerWidth < 768 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mobileMediaQuery = window.matchMedia('(max-width: 767px)')
    const motionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const updatePreferences = () => {
      const isMobile = mobileMediaQuery.matches
      const prefersReducedMotion = motionMediaQuery.matches
      const shouldAnimate = isMobile && !prefersReducedMotion

      setPreferences({
        isMobile,
        prefersReducedMotion,
        shouldAnimate
      })
    }

    updatePreferences()

    mobileMediaQuery.addEventListener('change', updatePreferences)
    motionMediaQuery.addEventListener('change', updatePreferences)

    return () => {
      mobileMediaQuery.removeEventListener('change', updatePreferences)
      motionMediaQuery.removeEventListener('change', updatePreferences)
    }
  }, [])

  return preferences
}

export const useShouldAnimate = (): boolean => {
  const { shouldAnimate } = useReducedMotion()
  return shouldAnimate
}
